// useAgentPortfolio — the signed-in user's agentic Robinhood mirror
// (agent_holdings + agent_decisions) from /api/agent-portfolio.
//
// Shaped after useLivePrices (it SURFACES error) rather than useLeveragedEtfs
// (which swallows it): the Agent Desk must distinguish "no holdings" (empty
// arrays, error=null) from "fetch failed" (empty arrays, error set). Arrays
// have defaults so consumers never null-check.
//
// Auth: attaches the Clerk session token as `Authorization: Bearer <token>`
// (same as usePortfolioTickers). Signed-out → no fetch, empty shape.
//   - signedIn=false                 → holdings/decisions=[], loading=false
//   - signedIn=true, loading=true    → request in flight
//   - loading=false, error=null      → holdings/decisions are the final lists
//   - loading=false, error=Error     → fetch/endpoint failed (page shows error)

import { useEffect, useState } from 'react'
import { useAuth, useUser } from '@clerk/clerk-react'

const EMPTY = { holdings: [], decisions: [], account: null, syncedAt: null }

export function useAgentPortfolio() {
  const { user, isLoaded } = useUser()
  const { getToken } = useAuth()
  const [data, setData] = useState(EMPTY)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isLoaded) return
    if (!user?.id) {
      setData(EMPTY)
      setLoading(false)
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const token = await getToken()
        if (cancelled) return
        const r = await fetch('/api/agent-portfolio', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        const j = await r.json().catch(() => ({}))
        if (cancelled) return
        if (!r.ok) {
          setData(EMPTY)
          setError(new Error(j?.error || `agent-portfolio HTTP ${r.status}`))
          setLoading(false)
          return
        }
        // Degrade-to-200 body carries `error` on a Supabase-level failure —
        // keep the empty shape but surface the error so the page doesn't show
        // an empty account as if the read succeeded.
        setData({
          holdings: j.holdings ?? [],
          decisions: j.decisions ?? [],
          account: j.account ?? null,
          syncedAt: j.syncedAt ?? null,
        })
        setError(j.error ? new Error(j.error) : null)
        setLoading(false)
      } catch (e) {
        if (!cancelled) {
          setData(EMPTY)
          setError(e)
          setLoading(false)
        }
      }
    })()
    return () => { cancelled = true }
    // getToken is stable per Clerk session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, user?.id])

  return {
    holdings: data.holdings,
    decisions: data.decisions,
    account: data.account,
    syncedAt: data.syncedAt,
    loading,
    error,
    signedIn: !!user?.id,
  }
}
